/**
 * The SMS and emails sent to people about their requests and changes — as a
 * list anyone in HR can read, with why a message did not go and a way to
 * send it again. Used on the Messages screen and in each staff file.
 */
import { useState } from "react";
import { useHrRetryNotification } from "../../api/queries";
import { Button, EmptyState } from "../../design/components";
import { NOTIFY_EVENT, NOTIFY_STATUS, type HrChannels, type HrNotification } from "../../domain/hrClock";
import { t } from "../../i18n";
import { Chip, h, PersonLink } from "./kit";
import { s } from "./shared";

export function Channels({ c }: { c: HrChannels }) {
  return (
    <div className={h.stats}>
      <div
        className={h.stat}
        style={{ ["--tint" as string]: c.sms.configured ? "var(--green)" : "var(--amber)", cursor: "default" }}
        data-testid="hr-channel-sms"
      >
        <span className={h.statN}>📱 {c.sms.configured ? t("Ready") : t("Not set up")}</span>
        <span className={h.statLabel}>
          {c.sms.configured
            ? t("SMS through {p}, sender {s}", { p: c.sms.provider ?? "", s: c.sms.sender ?? "" })
            : t(
                "SMS needs a Saudi SMS provider and a sender name registered with the CST Commission.",
              )}
        </span>
      </div>
      <div
        className={h.stat}
        style={{ ["--tint" as string]: c.email.configured ? "var(--green)" : "var(--amber)", cursor: "default" }}
        data-testid="hr-channel-email"
      >
        <span className={h.statN}>✉ {c.email.configured ? t("Ready") : t("Not set up")}</span>
        <span className={h.statLabel}>
          {c.email.configured
            ? t("Email from {f}", { f: c.email.from ?? "" })
            : t("Email needs the skylogics.ai Microsoft 365 mailbox or an SMTP server.")}
        </span>
      </div>
    </div>
  );
}

export function MessageList({ items, showPerson = true }: { items: HrNotification[]; showPerson?: boolean }) {
  const retry = useHrRetryNotification();
  const [open, setOpen] = useState<string | null>(null);
  if (!items.length) return <EmptyState>{t("No message yet.")}</EmptyState>;
  return (
    <ul className={h.list}>
      {items.map((n) => (
        <li key={n.id} className={h.item} style={{ flexWrap: "wrap" }} data-testid="hr-message">
          <span style={{ fontSize: 20 }} aria-hidden="true">
            {n.channel === "sms" ? "📱" : "✉"}
          </span>
          <span className={`${h.itemMain} ${h.itemClick}`} onClick={() => setOpen(open === n.id ? null : n.id)}>
            <b>
              {showPerson && (
                <>
                  <PersonLink id={n.employeeId} name={n.name} /> ·{" "}
                </>
              )}
              {t(NOTIFY_EVENT[n.event] ?? n.event)}
            </b>
            <span>
              {n.toAddress ?? t("no contact on file")} · {new Date(n.createdAt).toLocaleString()}
              {n.error ? ` · ${t(n.error)}` : ""}
            </span>
          </span>
          <Chip colour={NOTIFY_STATUS[n.status].colour}>{t(NOTIFY_STATUS[n.status].label)}</Chip>
          {n.status !== "sent" && n.toAddress && n.status !== "queued" && (
            <Button size="sm" onClick={() => retry.mutate(n.id)} disabled={retry.isPending}>
              {t("Send again")}
            </Button>
          )}
          {open === n.id && (
            <pre
              className={s.note}
              style={{ flexBasis: "100%", whiteSpace: "pre-wrap", margin: 0, unicodeBidi: "plaintext" }}
            >
              {n.subject ? `${n.subject}\n\n` : ""}
              {n.body}
            </pre>
          )}
        </li>
      ))}
    </ul>
  );
}
